import React, { useState, useRef } from "react";
import "../../css/components/reusable/FileUpload.css";

const FileUpload = ({
    name,
    label = "Upload File",
    acceptType = "both",
    maxFiles = 1,
    maxSizeMB = 1,
    onChange,
    onSave,
    onClose,
    inline = false,
}) => {
    const [files, setFiles] = useState([]);
    const [error, setError] = useState("");

    const inputRef = useRef(null);

    const MAX_SIZE = maxSizeMB * 1024 * 1024;

    /*
    |--------------------------------------------------------------------------
    | Upload Text
    |--------------------------------------------------------------------------
    */

    const getUploadText = () => {
        switch (acceptType) {
            case "image":
                return "Images can be uploaded";

            case "pdf":
                return "PDFs can be uploaded";

            case "excel":
                return "Excel files (.xls, .xlsx) can be uploaded";

            case "both":
            default:
                return "PDF and Images can be uploaded";
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Accepted MIME Types
    |--------------------------------------------------------------------------
    */

    const getAcceptedTypes = () => {
        switch (acceptType) {
            case "image":
                return [
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                ];

            case "pdf":
                return [
                    "application/pdf",
                ];

            case "excel":
                return [
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ];

            case "both":
            default:
                return [
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                    "application/pdf",
                ];
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Input accept attribute
    |--------------------------------------------------------------------------
    */

    const getAcceptAttr = () => {
        switch (acceptType) {
            case "image":
                return "image/*";

            case "pdf":
                return ".pdf";

            case "excel":
                return ".xlsx,.xls";

            case "both":
            default:
                return "image/*,.pdf";
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Validate File
    |--------------------------------------------------------------------------
    */

    const validateFile = (file) => {
        if (!file) {
            return "Invalid file";
        }

        if (!getAcceptedTypes().includes(file.type)) {
            return "Invalid file type";
        }

        if (file.size > MAX_SIZE) {
            return `File must be less than ${maxSizeMB} MB`;
        }

        return "";
    };

    /*
    |--------------------------------------------------------------------------
    | Handle Selected Files
    |--------------------------------------------------------------------------
    */

    const handleFiles = (selectedFiles) => {
        let newFiles = [...files];
        let validationError = "";

        for (const file of selectedFiles) {
            if (newFiles.length >= maxFiles) {
                validationError = `Max ${maxFiles} file(s) allowed`;
                break;
            }

            const validationMessage = validateFile(file);

            if (validationMessage) {
                validationError = validationMessage;
                continue;
            }

            newFiles.push(file);
        }

        setFiles(newFiles);
        setError(validationError);

        /*
        |--------------------------------------------------------------------------
        | Existing onChange functionality
        |--------------------------------------------------------------------------
        */

        if (onChange) {
            onChange(
                name,
                maxFiles === 1
                    ? newFiles[0]
                    : newFiles
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Drag & Drop
    |--------------------------------------------------------------------------
    */

    const handleDrop = (e) => {
        e.preventDefault();

        const droppedFiles = Array.from(
            e.dataTransfer.files || []
        );

        handleFiles(droppedFiles);
    };

    /*
    |--------------------------------------------------------------------------
    | File Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {
        const selectedFiles = Array.from(
            e.target.files || []
        );

        handleFiles(selectedFiles);

        /*
        |--------------------------------------------------------------------------
        | Allows selecting the same file again
        |--------------------------------------------------------------------------
        */

        e.target.value = "";
    };

    /*
    |--------------------------------------------------------------------------
    | Remove File
    |--------------------------------------------------------------------------
    */

    const removeFile = (index) => {
        const updatedFiles = files.filter(
            (_, fileIndex) => fileIndex !== index
        );

        setFiles(updatedFiles);

        setError("");

        if (onChange) {
            onChange(
                name,
                maxFiles === 1
                    ? updatedFiles[0]
                    : updatedFiles
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Clear Files
    |--------------------------------------------------------------------------
    */

    const clearFiles = () => {
        setFiles([]);
        setError("");
    };

    /*
    |--------------------------------------------------------------------------
    | Cancel
    |--------------------------------------------------------------------------
    */

    const handleCancel = () => {
        clearFiles();

        if (onClose) {
            onClose();
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Save
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | This component does NOT perform the API request.
    |
    | It simply passes the selected File to the parent.
    |
    */

    const handleSaveClick = () => {
        console.log("FileUpload -> Save clicked");

        if (!files.length) {
            console.log("FileUpload -> No file selected");

            setError("Please upload a file");

            return;
        }

        const fileToSave =
            maxFiles === 1
                ? files[0]
                : files;

        console.log(
            "FileUpload -> File to save:",
            fileToSave
        );

        /*
        |--------------------------------------------------------------------------
        | Pass selected file to parent
        |--------------------------------------------------------------------------
        */

        if (onSave) {
            onSave(fileToSave);
        }

        /*
        |--------------------------------------------------------------------------
        | Close/clear the FileUpload component itself
        |--------------------------------------------------------------------------
        |
        | This does NOT close CreateInvoice.
        |
        | In inline mode, CreateInvoice remains open.
        |
        */

        clearFiles();

        if (onClose) {
            onClose();
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Upload Content
    |--------------------------------------------------------------------------
    */

    const uploadContent = (
        <>
            <h3>{label}</h3>

            <div
                className={`file-upload-modal-upload-box ${
                    files.length
                        ? "file-upload-modal-has-file"
                        : ""
                }`}
                onClick={() => {
                    inputRef.current?.click();
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                }}
                onDrop={handleDrop}
            >
                {files.length === 0 ? (
                    <div className="file-upload-modal-upload-placeholder">
                        <p>
                            <strong>
                                Click or Drag & Drop
                            </strong>
                        </p>

                        <span>
                            {getUploadText()}
                            <br />
                            Max Size: {maxSizeMB}MB
                        </span>
                    </div>
                ) : (
                    <div className="file-upload-modal-file-list">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="file-upload-modal-file-preview"
                            >
                                {file.type.includes("image") ? (
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt="preview"
                                    />
                                ) : (
                                    <div className="file-upload-modal-pdf-box">
                                        📄
                                    </div>
                                )}

                                <div className="file-upload-modal-file-info">
                                    <p>{file.name}</p>

                                    <span>
                                        {(
                                            file.size / 1024
                                        ).toFixed(1)}{" "}
                                        KB
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    className="file-upload-modal-remove-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        removeFile(index);
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={getAcceptAttr()}
                    multiple={maxFiles > 1}
                    onChange={handleChange}
                    hidden
                />
            </div>

            {error && (
                <p className="file-upload-modal-error">
                    {error}
                </p>
            )}

            <div className="file-upload-modal-actions">
                <button
                    type="button"
                    onClick={handleCancel}
                >
                    Cancel
                </button>

                <button
                    type="button"
                    onClick={handleSaveClick}
                >
                    Save
                </button>
            </div>
        </>
    );

    /*
    |--------------------------------------------------------------------------
    | INLINE MODE
    |--------------------------------------------------------------------------
    |
    | Only used when:
    |
    | <FileUpload inline={true} />
    |
    */

    if (inline) {
        return (
            <div className="file-upload-inline">
                {uploadContent}
            </div>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | DEFAULT POPUP MODE
    |--------------------------------------------------------------------------
    |
    | Existing users of FileUpload don't need to change anything.
    |
    */

    return (
        <div
            className="file-upload-modal-overlay"
            onClick={handleCancel}
        >
            <div
                className="file-upload-modal"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {uploadContent}
            </div>
        </div>
    );
};

export default FileUpload;
